import { ChangeDetectionStrategy, Component, OnInit } from "@angular/core";
import { IPDFDocumentDto } from "core/dto";
import { SnackbarService } from "core/services";
import { DsgDeepTranslationKey } from "projects/designer/src/app/pipes/translate.pipe";
import { Subject, combineLatest, filter, from, map, switchMap, takeUntil } from "rxjs";
import { ProcessStepperService } from "../../../../process-stepper.service";
import { SignProcessWizardService } from "../../../../sign-process-wizard.service";
import { DocumentCollectionService } from "../document-collection.service";


@Component({
  selector: 'ss-file-drag-drop',
  templateUrl: './file-drag-drop.component.html',
  host: {
    class: 'flex-column gap-standard'
  },
  styleUrls: ['./file-drag-drop.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileDragDropComponent implements OnInit {

  private readonly _destroy = new Subject<void>();
  readonly processIdSubject = this._signProcessWizardService.processIdSubject;

  readonly filesDroppedSubject = new Subject<FileList>();
  readonly pdfDocumentsSubject = this._documentCollectionService.pdfDocumentsSubject;
  readonly triedNextSubject = this._processStepperService.triedNextSubject;

  readonly showErrorObs = combineLatest([
    this.triedNextSubject.pipe(map(({ documentUpload }) => documentUpload)),
    this.pdfDocumentsSubject.pipe(filter(Boolean), map(pdfDocuments => pdfDocuments.length))
  ])
    .pipe(map(([triedToNext, documentsLength]) => triedToNext && !documentsLength))

  background!: string;
  opacity = '1';

  readonly dragDropHeightObs = this._documentCollectionService.dragDropHeightObs;

  constructor(
    private readonly _documentCollectionService: DocumentCollectionService,
    private readonly _signProcessWizardService: SignProcessWizardService,
    private readonly _processStepperService: ProcessStepperService,
    private readonly _snackbarService: SnackbarService<DsgDeepTranslationKey>
  ) {

    this.filesDroppedSubject
      .pipe(

        switchMap(droppedFiles => from(this._fileListToPdfDocuments(droppedFiles))),

        filter(Boolean),
        takeUntil(this._destroy)
      )
      .subscribe((pdfDocuments) => {
        this._documentCollectionService.insertBatch(pdfDocuments)
      })
  }

  ngOnInit(): void {
  }

  dragOver(evParams: any) {
    evParams.preventDefault();
    evParams.stopPropagation();

    this.background = '#9ecbec';
    this.opacity = '0.8';
  }

  dragLeave(evParams: any) {
    evParams.preventDefault();
    evParams.stopPropagation();

    this.background = '#f5fcff';
    this.opacity = '1';
  }

  drop(evParams: DragEvent) {
    evParams.preventDefault();
    evParams.stopPropagation();

    this.background = '#f5fcff';
    this.opacity = '1';

    const files = evParams.dataTransfer?.files;
    if (files && files?.length > 0) {
      this.filesDroppedSubject.next(files);
    }
  }

  uploadFiles(evParams: Event) {

    const target = evParams.target as HTMLInputElement;
    const files = target.files;

    if (!files) {
      return;
    }

    this.filesDroppedSubject.next(files);

  }



  //todo file utils
  private async _fileListToPdfDocuments(files: FileList) {



    const processId = this.processIdSubject.value!;

    const fileArray = this._fileListToArray(files);
    const nonPdfFiles = fileArray.filter((file) => !file.name.endsWith('.pdf'));

    if (nonPdfFiles.length === fileArray.length) {

      this._snackbarService.open(
        {
          type: 'error',
          message: 'ss-process-wizard.error.no-pdf-documents'
        },
        {
          verticalPosition: 'top',
          horizontalPosition: 'right'
        }
      )

      return null;
    }

    if (nonPdfFiles.length) {
      this._snackbarService.open({
        type: 'info',
        message: 'ss-process-wizard.error.non-pdf-documents-removed',
      },
        {
          verticalPosition: 'top',
          horizontalPosition: 'right'
        }
      )



    }
    const pdfFiles = fileArray.filter((file) => file.name.endsWith('.pdf'));

    const pdfDocuments: IPDFDocumentDto[] = [];
    let idx = 0;

    for (const file of pdfFiles) {

      const byteArray = await this._streamToUInt8Array(file.stream());
      const base64 = btoa(byteArray.reduce((base64, byte) => base64 + String.fromCharCode(byte), ''));

      pdfDocuments.push({
        name: file.name,
        orderIndex: idx,
        processId,
        content: base64,
        mimeType: 'application/pdf'
      })

      idx++;
    }


    return pdfDocuments;
  }

  //array buffers need to have a single reference so garbage collector doesn't detach them
  private async _streamToUInt8Array(stream: ReadableStream<Uint8Array>) {
    const reader = stream.getReader();
    const chunks = [];
    let totalLength = 0;

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;
      chunks.push(value);
      totalLength += value.length;
    }

    const result = new Uint8Array(totalLength);
    let offset = 0;

    for (const chunk of chunks) {
      result.set(chunk, offset);
      offset += chunk.length;
    }

    return result;
  }

  private _fileListToArray(fileList: FileList) {
    const retArray: File[] = [];

    for (let fileIdx = 0; fileIdx < fileList.length; fileIdx++) {
      const file = fileList.item(fileIdx)!;
      retArray.push(file)
    }

    return retArray;
  }



  ngOnDestroy() {
    this._destroy.next();

    this._destroy.complete();
    this.filesDroppedSubject.complete();
  }
}

